import React, { useState } from "react";
import { Box, Grid, TextField, IconButton, Typography } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { SURFACE } from "../../../dark-theme/palette";
import Logger from "../../../services/Logger";
import { findWordInGrid } from "../../puzzles/common/utils/PuzzlePlayLogic";
import CrossWordCluesPanel from "../../puzzles/crossword/components/CrossWordCluesPanel";
import { usePagesTranslation } from "../../locales";

const StaticCrossWordsPanelSection = ({ samplePuzzle, dictionaryLanguage = "english" }) => {
    const { t } = usePagesTranslation();
    const [selectedWord, setSelectedWord] = useState(null);
    const [, setHighlightCells] = useState([]);

    // Parse filledCoordinates from template
    let filledCoordinates = [];
    if (samplePuzzle?.template?.filledCoordinates) {
        try {
            filledCoordinates = JSON.parse(samplePuzzle.template.filledCoordinates);
        } catch (error) {
            Logger.error('Error parsing filledCoordinates:', error);
            filledCoordinates = [];
        }
    }

    // Handle word selection from panel
    const handleWordSelect = (word) => {
        setSelectedWord(word);
        // Highlight the word on the grid
        if (word && filledCoordinates) {
            const cells = findWordInGrid(word, filledCoordinates);
            if (cells && cells.length > 0) {
                // Convert cells to highlightCells format
                const highlightCellsSameColor = cells.map((cell) => ({
                    row: cell.row,
                    col: cell.col
                }));
                setHighlightCells(highlightCellsSameColor);
            } else {
                setHighlightCells([]);
            }
        } else {
            setHighlightCells([]);
        }
    };

    return (
        <Box sx={{ mb: 5, width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                {t("howto.crossword.words-panel-title")}
            </Typography>
            <Grid container spacing={3} sx={{ width: '100%', maxWidth: '100%', margin: 0 }}>
                {/* First Column - Words Panel Component */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Box sx={{ 
                            width: '100%', 
                            maxWidth: '400px',
                            backgroundColor: SURFACE.sunken,
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            <CrossWordCluesPanel
                                filledCoordinates={filledCoordinates}
                                wordsFound={[]}
                                onHide={() => {}}
                                selectedWord={selectedWord}
                                selectedWords={selectedWord ? [selectedWord] : []}
                                onWordSelect={handleWordSelect}
                                hideAllWords={true}
                                language={dictionaryLanguage}
                                maxWords={6}
                                useCacheEndpoint={true}
                            />
                        </Box>
                    </Box>
                </Grid>
                
                {/* Second Column - Description */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                        {t("howto.crossword.words-panel-intro")}
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mb: 3 }}>
                        <li>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.words-panel-across-down")}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.words-panel-word-numbers")}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.words-panel-progress")}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.words-panel-meanings")}
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.crossword.words-panel-enter-word")}
                            </Typography>
                        </li>
                    </Box>

                    <Typography variant="body1" sx={{ fontSize: "1rem", lineHeight: 1.6, mb: 1 }}>
                        {t("howto.crossword.words-panel-popup-title")}
                    </Typography>
                    <Box
                        sx={{
                            p: 1.5,
                            minWidth: 220,
                            maxWidth: 280,
                            backgroundColor: SURFACE.card,
                            borderRadius: 1,
                            border: `1px solid ${SURFACE.border}`,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            mb: 2
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    minWidth: 28,
                                    minHeight: 28,
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#C4FFE0",
                                    borderRadius: "50%",
                                    border: "1px solid #2E8B57",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    color: "#000",
                                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                                }}
                            >
                                1
                            </Box>
                            <TextField
                                size="small"
                                placeholder={t("howto.crossword.words-panel-placeholder")}
                                disabled
                                sx={{ flex: 1 }}
                                slotProps={{ htmlInput: { "aria-label": "Enter word (demo)" } }}
                            />
                            <IconButton size="small" color="primary" sx={{ flexShrink: 0 }} disabled>
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            {t("howto.crossword.words-panel-submit-hint")}
                        </Typography>
                    </Box>

                    <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                        {t("howto.crossword.words-panel-intersect")}
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, backgroundColor: SURFACE.sunken, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
                            {t("howto.crossword.words-panel-note-prefix")}{' '}
                            <Box
                                component="a"
                                href="https://en.wiktionary.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: "#3BA666",
                                    textDecoration: "none",
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t("howto.crossword.words-panel-wiktionary")}
                            </Box>
                            {' '}{t("howto.crossword.words-panel-note-between")}{' '}
                            <Box
                                component="a"
                                href="https://creativecommons.org/licenses/by-sa/3.0/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    color: "#3BA666",
                                    textDecoration: "none",
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {t("howto.crossword.words-panel-license")}
                            </Box>
                            {' '}{t("howto.crossword.words-panel-note-suffix")}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default StaticCrossWordsPanelSection;

